import { Outlet } from 'react-router-dom';
import { AppShell, AnimatedPage, type NavSection } from '@theonefamily/ui';
import { HubBar } from '@theonefamily/hub-bar';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useApi } from '@/hooks/useApi';
import {
  LayoutDashboard, Brain, Users, Network,
  UserSearch, Briefcase, UserPlus, UserMinus, ScanSearch, FileCheck, Globe,
  GraduationCap, GitBranch, Grid3X3, Star, MessageSquare,
  Dna, FlaskConical, TrendingDown, Clock, Zap, Wand2,
  ClipboardList, Activity, Crown,
  DollarSign, PieChart, Heart,
  Calendar, Shield, FileSignature, BarChart3, Settings,
} from 'lucide-react';

const I = (icon: unknown) => icon as NavSection['items'][0]['icon'];

const NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: I(LayoutDashboard), href: '/' },
      { id: 'theo-hr',   label: 'THEO HR',   icon: I(Brain),           href: '/theo-hr' },
      { id: 'employees', label: 'Employees', icon: I(Users),           href: '/employees' },
      { id: 'org-chart', label: 'Org Chart', icon: I(Network),         href: '/org-chart' },
    ],
  },
  {
    label: 'Talent',
    items: [
      { id: 'candidates',    label: 'Candidates',       icon: I(UserSearch), href: '/candidates' },
      { id: 'jobs',          label: 'Job Postings',     icon: I(Briefcase),  href: '/jobs' },
      { id: 'onboarding',    label: 'Onboarding',       icon: I(UserPlus),   href: '/onboarding' },
      { id: 'offboarding',   label: 'Offboarding',      icon: I(UserMinus),  href: '/offboarding' },
      { id: 'background-checks', label: 'Background Checks', icon: I(ScanSearch), href: '/hr/background-checks' },
      { id: 'i9-everify',    label: 'I-9 / E-Verify',  icon: I(FileCheck),  href: '/i9-everify' },
      { id: 'global-workforce', label: 'Global Workforce', icon: I(Globe),   href: '/global-workforce' },
    ],
  },
  {
    label: 'Development',
    items: [
      { id: 'training',      label: 'Training',     icon: I(GraduationCap), href: '/training' },
      { id: 'career-paths',  label: 'Career Paths', icon: I(GitBranch),     href: '/career-paths' },
      { id: 'skills-matrix', label: 'Skills Matrix',icon: I(Grid3X3),       href: '/skills-matrix' },
      { id: 'reviews',       label: 'Reviews',      icon: I(Star),          href: '/reviews' },
      { id: 'peer-feedback', label: 'Peer Feedback',icon: I(MessageSquare), href: '/peer-feedback' },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { id: 'workdna',                  label: 'WorkDNA',         icon: I(Dna),         href: '/workdna' },
      { id: 'team-chemistry',           label: 'Team Chemistry',  icon: I(FlaskConical),href: '/team-chemistry' },
      { id: 'attrition-model',          label: 'Attrition Risk',  icon: I(TrendingDown),href: '/attrition-model' },
      { id: 'shift-intelligence',       label: 'Shift Intel',     icon: I(Clock),       href: '/shift-intelligence' },
      { id: 'candidate-sourcing',       label: 'Sourcing Intel',  icon: I(Zap),         href: '/candidate-sourcing' },
      { id: 'job-description-generator',label: 'JD Generator',    icon: I(Wand2),       href: '/job-description-generator' },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { id: 'pulse-surveys',      label: 'Pulse Surveys', icon: I(ClipboardList), href: '/pulse-surveys' },
      { id: 'engagement',         label: 'Engagement',    icon: I(Activity),      href: '/engagement' },
      { id: 'succession-planning',label: 'Succession',    icon: I(Crown),         href: '/succession-planning' },
    ],
  },
  {
    label: 'Compensation',
    items: [
      { id: 'compensation',               label: 'Compensation', icon: I(DollarSign), href: '/compensation' },
      { id: 'compensation-intelligence',  label: 'Comp Intel',   icon: I(PieChart),   href: '/compensation-intelligence' },
      { id: 'benefits',                   label: 'Benefits',     icon: I(Heart),      href: '/benefits' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { id: 'time-off',  label: 'Time Off',    icon: I(Calendar),      href: '/time-off' },
      { id: 'policies',  label: 'Policies',    icon: I(Shield),        href: '/policies' },
      { id: 'esign',     label: 'E-Signature', icon: I(FileSignature), href: '/esign' },
      { id: 'reports',   label: 'Reports',     icon: I(BarChart3),     href: '/reports' },
      { id: 'admin',     label: 'Admin',       icon: I(Settings),      href: '/admin' },
    ],
  },
];

interface AuthMe {
  userId: string;
  email: string;
  tenantId: string;
  role: string;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  hr_manager: 'HR Manager',
  hr_staff: 'HR Staff',
  manager: 'Manager',
  viewer: 'Viewer',
};

export function Layout() {
  const { isOnline } = useNetworkStatus();
  const { data: authMe } = useApi<AuthMe>('auth/me');

  const userName = authMe?.email?.split('@')[0]?.replace(/[._-]/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) ?? 'User';
  const userEmail = authMe?.email ?? '';
  const userRole = authMe?.role ? (ROLE_LABELS[authMe.role] ?? authMe.role) : 'User';

  return (
    <>
      {!isOnline && (
        <div className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 text-center text-sm font-medium">
          You&apos;re offline — changes will sync when reconnected
        </div>
      )}
      <HubBar
        currentProduct="people"
        apiBase={import.meta.env.VITE_BUS_URL ?? 'https://api.theonestack.com'}
        hubUrl={import.meta.env.VITE_HUB_URL ?? 'https://app.theonestack.com'}
        supportConfig={{
          apiBaseUrl: import.meta.env.VITE_OPS_CENTER_API_URL ?? 'https://api.theonestack.com',
          apiKey: import.meta.env.VITE_OPS_CENTER_API_KEY ?? '',
          platformId: 'people',
          platformName: 'The One People',
        }}
      />
      <AppShell
        product={{ name: 'The One People', accent: '#8b5cf6' }}
        navigation={NAVIGATION}
        user={{ name: userName, email: userEmail, role: userRole }}
        onSignOut={() => {
          void fetch(`${import.meta.env.VITE_API_URL ?? 'https://api.theonestack.com'}/api/people/auth/logout`, { method: 'POST', credentials: 'include' })
            .finally(() => { window.location.href = import.meta.env.VITE_HUB_URL ?? 'https://app.theonestack.com'; });
        }}
      >
        <AnimatedPage>
          <Outlet />
        </AnimatedPage>
      </AppShell>
    </>
  );
}
